/**
 * One `interaction_request` part: renders the request's fields and actions and submits
 * the user's response. Split out of ConversationMessageParts.tsx (500-line cap).
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/Button.js';
import { Input, Select, Textarea } from '../ui/Input.js';
import { Toggle } from '../ui/Toggle.js';
import {
  type ConversationInteractionAction,
  type ConversationInteractionField,
  type ConversationInteractionRequest,
  type ConversationInteractionSubmission,
  type ConversationMessagePart,
} from '@confluent/shared/lib/conversation-interactions';
import {
  actionVariant,
  defaultFieldValue,
  isActionableConversationAction,
  getInteractionResolutionMeta,
  getInteractionActionPendingText,
  prepareInteractionSubmission,
} from './conversation-interaction-helpers.js';

export function InteractionCard({
  part,
  sourceMessageId,
  disabled,
  onSubmit,
}: {
  part: Extract<ConversationMessagePart, { type: 'interaction_request' }>;
  sourceMessageId: string;
  disabled?: boolean;
  onSubmit: (input: {
    interactionId: string;
    request: ConversationInteractionRequest;
    submission: ConversationInteractionSubmission;
  }) => void;
}) {
  const request = part.request;
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(request.fields.map((field) => [field.id, defaultFieldValue(field)])),
  );
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const isResolved = part.status !== 'open';
  const isDisabled = isResolved || disabled === true;
  const actions: ConversationInteractionAction[] =
    request.actions.length > 0
      ? request.actions
      : [{ id: 'submit', label: request.submitLabel, kind: 'submit', style: 'primary' }];
  const pendingAction = actions.find((action) => action.id === pendingActionId) ?? null;
  const resolutionMeta = isResolved && part.status !== 'open' ? getInteractionResolutionMeta(part.status) : null;

  useEffect(() => {
    if (!disabled && !isResolved) setPendingActionId(null);
  }, [disabled, isResolved]);

  const updateValue = (fieldId: string, value: unknown) => {
    if (validationMessage) setValidationMessage(null);
    setValues((current) => ({ ...current, [fieldId]: value }));
  };

  const handleAction = (action: ConversationInteractionAction) => {
    setValidationMessage(null);
    setPendingActionId(action.id);
    try {
      const submission = prepareInteractionSubmission({
        interactionId: part.interactionId,
        sourceMessageId,
        request,
        values,
        action,
      });
      onSubmit({
        interactionId: submission.interactionId,
        request,
        submission,
      });
    } catch (error) {
      setPendingActionId(null);
      setValidationMessage((error as Error).message ?? 'Unable to submit this response.');
    }
  };

  const renderField = (field: ConversationInteractionField) => {
    const value = values[field.id];
    if (
      field.kind === 'single_choice' ||
      field.kind === 'select' ||
      field.kind === 'radio' ||
      field.kind === 'combobox'
    ) {
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          disabled={isDisabled}
          onChange={(event) => updateValue(field.id, event.target.value)}
        >
          <option value="">Choose...</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      );
    }

    if (field.kind === 'multi_choice' || field.kind === 'checkboxes') {
      const selected = Array.isArray(value) ? value.map(String) : [];
      return (
        <div className="flex flex-wrap gap-2">
          {field.options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <Button
                key={option.value}
                variant={checked ? 'primary' : 'secondary'}
                size="sm"
                aria-pressed={checked}
                disabled={isDisabled}
                onClick={() =>
                  updateValue(
                    field.id,
                    checked ? selected.filter((item) => item !== option.value) : [...selected, option.value],
                  )
                }
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      );
    }

    if (field.kind === 'boolean') {
      return (
        <Toggle
          checked={value === true}
          disabled={isDisabled}
          label={field.label}
          onChange={(checked) => updateValue(field.id, checked)}
        />
      );
    }

    if (field.kind === 'number' || field.kind === 'currency' || field.kind === 'percent' || field.kind === 'slider') {
      return (
        <Input
          type="number"
          value={typeof value === 'number' || typeof value === 'string' ? value : ''}
          disabled={isDisabled}
          onChange={(event) => updateValue(field.id, event.target.value === '' ? '' : Number(event.target.value))}
        />
      );
    }

    if (field.kind === 'textarea' || field.kind === 'rich_text' || field.kind === 'code') {
      return (
        <Textarea
          className="min-h-24"
          value={typeof value === 'string' ? value : ''}
          disabled={isDisabled}
          placeholder={'placeholder' in field ? field.placeholder : undefined}
          onChange={(event) => updateValue(field.id, event.target.value)}
        />
      );
    }

    return (
      <Input
        type={field.kind === 'date' ? 'date' : field.kind === 'time' ? 'time' : 'text'}
        value={typeof value === 'string' ? value : ''}
        disabled={isDisabled}
        placeholder={'placeholder' in field ? field.placeholder : undefined}
        onChange={(event) => updateValue(field.id, event.target.value)}
      />
    );
  };

  return (
    <div className="rounded-lg border bg-surface px-3 py-3 space-y-3">
      <div className="space-y-2">
        <p className="text-sm font-semibold text-heading">{request.title}</p>
        {request.body && <p className="text-sm text-body whitespace-pre-wrap">{request.body}</p>}
        {request.why && (
          <div className="rounded-md border border-border/70 bg-surface-alt px-2.5 py-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted">Why this matters</p>
            <p className="mt-1 text-xs text-body whitespace-pre-wrap">{request.why}</p>
          </div>
        )}
        {request.expectedOutput && (
          <div className="rounded-md border border-border/70 bg-surface-alt px-2.5 py-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted">Next step</p>
            <p className="mt-1 text-xs text-body whitespace-pre-wrap">{request.expectedOutput}</p>
          </div>
        )}
      </div>
      {request.fields.map((field) => (
        <div key={field.id} className="space-y-1.5">
          {field.kind !== 'boolean' && (
            <label className="block text-xs font-medium text-heading">
              {field.label}
              {field.required && <span className="text-danger"> *</span>}
            </label>
          )}
          {renderField(field)}
          {field.helpText && <p className="text-[11px] text-muted">{field.helpText}</p>}
        </div>
      ))}
      {validationMessage && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-2.5 py-2 text-xs text-danger">
          {validationMessage}
        </div>
      )}
      {isResolved && resolutionMeta ? (
        <div className={`rounded-md border px-2.5 py-2 ${resolutionMeta.className}`}>
          <p className="text-xs font-medium">{resolutionMeta.label}</p>
          <p className="mt-1 text-xs">{resolutionMeta.detail}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => {
              const actionable = isActionableConversationAction(action);
              const isActionPending = pendingActionId === action.id && disabled;
              return (
                <Button
                  key={action.id}
                  variant={actionVariant[action.style] ?? 'secondary'}
                  size="sm"
                  onClick={() => handleAction(action)}
                  disabled={!part.interactionId || isDisabled || !actionable}
                  title={actionable ? undefined : 'This action is not available in this client yet.'}
                >
                  {isActionPending ? getInteractionActionPendingText(action) : action.label}
                </Button>
              );
            })}
          </div>
          {pendingAction && disabled && (
            <p className="text-xs text-muted">{getInteractionActionPendingText(pendingAction)}</p>
          )}
        </div>
      )}
    </div>
  );
}
